<template>
  <Head title="Register" />
  <div class="fixed inset-0 flex light login-page overflow-hidden">
    <div class="absolute top-0 left-0 w-full z-50">
      <flash-messages />
    </div>

    <!-- LEFT: Branding panel (desktop only) -->
    <div class="hidden lg:flex lg:w-1/2 h-full relative items-center justify-center overflow-hidden login-brand">
      <div class="login-blob login-blob-1"></div>
      <div class="login-blob login-blob-2"></div>
      <div class="login-blob login-blob-3"></div>

      <div class="relative z-10 text-center px-12">
        <Link :href="route('home')"><logo name="white" class="block mx-auto w-full max-w-xs" height="50" /></Link>
        <h1 class="mt-10 text-3xl font-bold text-white leading-snug">
          {{ __('Kelola tiket dukungan Anda dengan lebih mudah') }}
        </h1>
      </div>
    </div>

    <!-- RIGHT -->
    <div class="w-full lg:w-1/2 h-full overflow-y-auto bg-white dark:bg-slate-900">
      <div class="min-h-full flex items-center justify-center p-6 sm:p-8">
        <div class="w-full max-w-2xl">
          <!-- Mobile logo -->
          <Link :href="route('home')" class="lg:hidden block mb-10">
            <logo class="block mx-auto w-full max-w-[150px]" height="50" />
          </Link>

          <!-- Success state -->
          <div v-if="$page.props.flash && $page.props.flash.success" class="login-card text-center">
            <div class="flex items-center justify-center w-16 h-16 mx-auto mb-5 rounded-full bg-green-100">
              <svg class="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-slate-800 dark:text-white">Terima kasih telah mendaftar!</h2>
            <p class="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Akun Anda sedang menunggu persetujuan admin. Anda akan bisa login setelah akun disetujui.
            </p>
            <Link :href="route('login')" class="login-submit inline-flex items-center justify-center mt-6 px-6 py-3 text-sm font-bold text-white rounded-xl">
              Kembali ke Login
            </Link>
          </div>

          <!-- Register form -->
          <form v-else class="login-card" @submit.prevent="login">
            <h2 class="text-2xl font-bold text-slate-800 dark:text-white">{{ __('Create your account') }}</h2>
            <p class="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {{ __('Already have an account?') }}
              <Link :href="route('login')" class="font-medium login-link">{{ __('Login') }}</Link>
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
              <text-input v-model="form.first_name" :error="form.errors.first_name" label="First Name" is_required />
              <text-input v-model="form.last_name" :error="form.errors.last_name" label="Last Name" is_required />
            </div>

            <text-input v-model="form.email" :error="form.errors.email" type="email" label="Email" is_required class="mt-5" />

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
              <div>
                <label class="form-label">Department</label>
                <select v-model="form.department_id" class="form-select mt-1" @change="form.channel_id = null">
                  <option :value="null">-- Pilih Department --</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
                </select>
                <div v-if="form.errors.department_id" class="form-error">{{ form.errors.department_id }}</div>
              </div>

              <div>
                <label class="form-label">Channel</label>
                <select v-model="form.channel_id" class="form-select mt-1" :disabled="!form.department_id || availableChannels.length === 0">
                  <option :value="null">{{ form.department_id && availableChannels.length === 0 ? 'Tidak ada channel' : '-- Pilih Channel --' }}</option>
                  <option v-for="ch in availableChannels" :key="ch.id" :value="ch.id">{{ ch.name }}</option>
                </select>
                <div v-if="form.errors.channel_id" class="form-error">{{ form.errors.channel_id }}</div>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
              <text-input v-model="form.password" :error="form.errors.password" type="password" label="Password" is_required />
              <text-input v-model="form.confirm_password" :error="form.errors.confirm_password" type="password" label="Confirm Password" is_required />
            </div>

            <!-- Live password match indicator -->
            <div v-if="form.confirm_password" class="mt-2 flex items-center gap-1.5 text-sm">
              <template v-if="passwordsMatch">
                <svg class="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                </svg>
                <span class="text-green-600 font-medium">Password cocok</span>
              </template>
              <template v-else>
                <svg class="w-4 h-4 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span class="text-red-500 font-medium">Password tidak cocok</span>
              </template>
            </div>

            <loading-button :loading="form.processing" type="submit" class="login-submit w-full items-center justify-center mt-8">
              {{ __('Create Account') }}
            </loading-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Logo from '@/Shared/Logo'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import FlashMessages from '@/Shared/FlashMessages'
import { Head, Link } from '@inertiajs/vue3'

export default {
  metaInfo: { title: 'Register' },
  components: {
    Logo,
    TextInput,
    LoadingButton,
    FlashMessages,
    Head,
    Link,
  },
  props: {
    is_demo: Number,
    departments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: this.$inertia.form({
        first_name: '',
        last_name: '',
        email: '',
        department_id: null,
        channel_id: null,
        password: '',
        confirm_password: '',
      }),
    }
  },
  computed: {
    passwordsMatch() {
      return this.form.password.length > 0 && this.form.password === this.form.confirm_password
    },
    availableChannels() {
      if (!this.form.department_id) return []
      const dept = this.departments.find(d => d.id === this.form.department_id)
      return dept ? dept.channels : []
    },
  },
  methods: {
    login() {
      if (this.form.password !== this.form.confirm_password) {
        alert('Your password is not matched correctly.')
        return
      }
      this.form.post(this.route('register.store'))
    },
  },
}
</script>

<style scoped>
.login-brand {
    background: linear-gradient(135deg, #0f3d3a 0%, #1e1b4b 60%, #16142e 100%);
}

.login-blob {
    position: absolute;
    border-radius: 9999px;
    filter: blur(90px);
    opacity: 0.35;
}

.login-blob-1 {
    width: 22rem;
    height: 22rem;
    background: #0f766e;
    top: -6rem;
    left: -6rem;
}

.login-blob-2 {
    width: 26rem;
    height: 26rem;
    background: #4338ca;
    bottom: -8rem;
    right: -8rem;
}

.login-blob-3 {
    width: 16rem;
    height: 16rem;
    background: #4f46e5;
    bottom: 4rem;
    left: 6rem;
}

.login-card {
    width: 100%;
}

.login-submit {
    background: linear-gradient(135deg, #0f766e 0%, #4f46e5 100%);
    border-radius: 0.75rem;
    padding: 0.75rem 1.25rem;
    font-weight: 700;
    color: #fff;
    box-shadow: 0 6px 20px rgba(79, 70, 229, 0.3);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.login-submit:hover,
.login-submit:focus {
    filter: brightness(1.08);
    box-shadow: 0 8px 24px rgba(79, 70, 229, 0.4);
    transform: translateY(-1px);
}

.login-link {
    color: #4f46e5;
}

.login-link:hover {
    color: #4338ca;
}
</style>
